/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { ChangeEvent } from "react";
import { Form, FastField, useFormikContext } from "formik";
import {
  Box,
  Button,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { makeStyles, createStyles } from "@mui/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import { FormValues } from "../BulkUpdate";
import * as Yup from "yup";
import { isEmpty } from "ramda";
import TimeInput from "../../formElements/TimeInput";

export type Option = { label: string; value: string | number | boolean | object };

export interface DynamicFieldProps {
  fieldType:
    | "text"
    | "number"
    | "select"
    | "checkbox"
    | "switch"
    | "autocomplete"
    | "timeinput"
    | "radio";
  label: string;
  name: string;
  options?: Option[];
  fullWidth?: boolean;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  validationSchema?:
    | Yup.StringSchema
    | Yup.NumberSchema
    | Yup.BooleanSchema
    | Yup.ArraySchema<any, unknown, unknown, any>
    | Yup.ObjectSchema<any, unknown, unknown, any>;
}

interface BulkUpdateDataProps {
  updateFields?: DynamicFieldProps[];
}

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      width: "100%",
    },
    addFieldBtn: {
      margin: "20px 0",
    },
    inputField: {
      width: 300,
    },
    selectDropDown: {
      width: 180,
    },
  })
);

const BulkUpdateData = (props: BulkUpdateDataProps) => {
  const classes = useStyles();
  const { updateFields } = props;
  const { values, errors, touched, setFieldValue } =
    useFormikContext<FormValues>();

  const renderField = (field: { name: string; index: number }) => {
    const fieldItem = updateFields?.find((item) => item.name === field.name);
    const fieldType = fieldItem?.fieldType;
    const options = fieldItem?.options || [];

    const isError =
      touched?.fields?.[field.index]?.value &&
      !isEmpty(errors) &&
      errors.fields &&
      errors.fields[field.index] &&
      // @ts-ignore
      errors.fields[field.index].value;

    // @ts-ignore
    const errorMessage = isError ? errors.fields[field.index].value : "";

    switch (fieldType) {
      case "text":
      case "number":
        const isTypeNumber = fieldType === "number";
        return (
          <FastField
            as={TextField}
            type={isTypeNumber ? "number" : "text"}
            name={`fields.${field.index}.value`}
            label="Value"
            variant="outlined"
            style={{ width: 300 }}
            error={isError}
            helperText={errorMessage}
          />
        );
      case "select":
        return (
          <Box className={classes.inputField as string}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Value</InputLabel>
              <FastField
                key={field.name}
                as={Select}
                name={`fields.${field.index}.value`}
                label="Value"
                variant="outlined"
                error={isError}
                helperText={errorMessage}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {options && options.length ? (
                  options.map((option, idx) => (
                    <MenuItem key={idx} value={option.value as string}>
                      {option.label}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                )}
              </FastField>
            </FormControl>
          </Box>
        );
      case "timeinput": {
        return (
          <Box className={classes.inputField as string}>
            <FormControl fullWidth variant="outlined">
              <TimeInput
                fieldname={`fields.${field.index}.value`}
                label="Value"
                onChange={(val) => {
                  setFieldValue(`fields.${field.index}.value`, val)
                }}
                value={values.fields[field.index].value as string}
              />
            </FormControl>
          </Box>
        )
      }
      default:
        // setFieldValue(`fields.${field.index}.value`, "");
        return null;
    }
  };

  const handleAddField = (values: FormValues) => {
    setFieldValue("fields", [...values.fields, {}]);
  };

  const handleRemoveField = (values: FormValues, index: number) => {
    setFieldValue(
      "fields",
      values.fields.filter((_, i) => i !== index)
    );
  };

  // keep this function for future update
  // const onChangeField = (e: ChangeEvent<{ value: string }>, index: number) => {
  //   const newFieldName = e.target.value;
  //   const newFields = [...values.fields];
  //   newFields[index] = { name: newFieldName, value: "" };
  //   setFieldValue("fields", newFields);
  // };

  const generateUniqueCode = (index: number) => {
    const timestamp = Date.now();
    const randomStr = Math.random().toString(36).substring(2, 15);
    return `${timestamp}_${randomStr}_${index}`;
  };

  return (
    <Box padding={4} paddingTop={1}>
      <Form>
        <Box>
          <h3>Add field</h3>
          <Typography>
            To add a field to the update process, simply click Add Field button
            below. This will add the field to the list of selected fields for
            updating. You can remove a field from the list by clicking on the
            Delete icon.
          </Typography>
          <Button
            variant="outlined"
            className={classes.addFieldBtn}
            onClick={() => handleAddField(values)}
            disabled={values.fields.length === updateFields?.length}
          >
            Add Field +
          </Button>
          <Divider />
        </Box>

        <Box>
          {values.fields.map((field, index) => (
            <Box key={index} marginTop={2} display="flex" alignItems="start">
              <Box
                className={classes.selectDropDown}
                marginRight={2}
                key={generateUniqueCode(index)}
              >
                <FormControl variant="outlined" fullWidth>
                  <InputLabel>Field</InputLabel>
                  <FastField
                    as={Select}
                    name={`fields.${index}.name`}
                    value={field.name || ""}
                    label="Field"
                    variant="outlined"
                    // onChange={(e: ChangeEvent<{ value: string }>) =>
                    //   onChangeField(e, index)
                    // }
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {updateFields &&
                      updateFields
                        .filter(
                          (item) =>
                            !values.fields.some(
                              (f, i) => i !== index && f.name === item.name
                            )
                        )
                        .map((item) => (
                          <MenuItem
                            key={generateUniqueCode(index)}
                            value={item.name}
                          >
                            {item.label}
                          </MenuItem>
                        ))}
                  </FastField>
                </FormControl>
              </Box>

              {field.name && renderField({ name: field.name, index })}

              {values.fields.length > 1 ? (
                <IconButton
                  aria-label="delete"
                  onClick={() => handleRemoveField(values, index)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              ) : null}
            </Box>
          ))}
        </Box>
      </Form>
    </Box>
  );
};

export default BulkUpdateData;
